import React from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useFamilyFeatures } from '@/hooks/useFamilyFeatures'
import { Gift, ClipboardCheck, CreditCard, Settings } from 'lucide-react'
import RewardsStore from '@/components/RewardsStore'
import RedemptionQueue from '@/components/RedemptionQueue'
import { GreenlightConverter } from '@/components/GreenlightConverter'
import { RewardsManager } from '@/components/admin/RewardsManager'

type RewardsTab = 'store' | 'redemptions' | 'greenlight' | 'manage'

/**
 * Rewards page - Shows rewards store and redemption queue
 * Route: /dashboard/rewards
 */
export const RewardsPage: React.FC = () => {
  const { user } = useAuth()
  // The tab follows ?tab= so links such as "Manage Rewards" open the right one.
  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('tab') as RewardsTab | null
  const activeTab: RewardsTab =
    requested && ['store', 'redemptions', 'manage', 'greenlight'].includes(requested) ? requested : 'store'
  const setActiveTab = (tab: RewardsTab) => setSearchParams(tab === 'store' ? {} : { tab }, { replace: true })
  // Greenlight is a per-family feature toggle (family_feature_toggles.greenlight)
  const { isFeatureEnabled } = useFamilyFeatures()
  const greenlightEnabled = isFeatureEnabled('greenlight')

  const tabs = [
    { id: 'store' as RewardsTab, label: 'Rewards Store', icon: Gift },
    { id: 'redemptions' as RewardsTab, label: 'Pending Approvals', icon: ClipboardCheck },
    { id: 'manage' as RewardsTab, label: 'Manage Rewards', icon: Settings },
    ...(greenlightEnabled ? [{ id: 'greenlight' as RewardsTab, label: 'Greenlight', icon: CreditCard }] : [])
  ]

  return (
    <div className="space-y-4">
      {/* Header with tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="px-6 py-4 border-b border-gray-100">
          <h1 className="text-xl font-bold text-gray-900">Rewards</h1>
        </div>

        {/* Tabs - only show for parents */}
        {user?.is_parent && (
          <div className="px-6 border-b border-gray-100">
            <nav className="flex space-x-6">
              {tabs.map((tab) => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors
                      ${activeTab === tab.id
                        ? 'border-amber-500 text-amber-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                )
              })}
            </nav>
          </div>
        )}
      </div>

      {/* Content */}
      <div>
        {activeTab === 'store' && <RewardsStore />}
        {activeTab === 'redemptions' && user?.is_parent && <RedemptionQueue />}
        {activeTab === 'manage' && user?.is_parent && <RewardsManager />}
        {activeTab === 'greenlight' && greenlightEnabled && user?.is_parent && <GreenlightConverter />}
      </div>
    </div>
  )
}
